import { Component, effect, input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MergeBehaviorModel, MergeBehaviorsModel, PatientBehaviorModel } from '../../core/api/models';
import { StaffApiService, SupervisorApiService } from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { Utils } from '../../core/utils';
import { SupervisorHelperService } from '../../supervisor/supervisor.helper.service';

@Component({
  selector: 'app-merge-behaviors',
  templateUrl: './merge-behaviors.component.html',
  standalone: false,
})
export class MergeBehaviorsComponent {
  patientID: string = '';
  patientIDLoaded: boolean = false;
  loading: boolean = false;

  behaviorsToMerge: {
    BehaviorIDToMerge?: number;
    BehaviorIDToMergeInto?: number;
    destinationBehaviors: PatientBehaviorModel[];
  }[] = [];
  sourceBehaviors: PatientBehaviorModel[] = [];
  destinationBehaviors: PatientBehaviorModel[] = [];
  allBehaviors: PatientBehaviorModel[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private staffApiService: StaffApiService,
    private supervisorApiService: SupervisorApiService,
    private supervisorHelperService: SupervisorHelperService,
    private alertService: AlertService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.patientID = params['patientID'];
      this.patientIDLoaded = true;
      if (!this.patientID) {
        this.router.navigate(['/select-patient']);
      }

      this.loading = true;
      const getBehaviorsObservable = this.staffApiService.GetPatientBehaviors({
        patientID: parseInt(this.patientID),
      });
      getBehaviorsObservable.subscribe({
        next: (behaviors: PatientBehaviorModel[]) => {
          this.initLoadBehaviors(behaviors);
          this.loading = false;
        },
        error: (error: any) => {
          this.loading = false;
          this.alertService.error('Error loading behaviors');
        }
      });
    });
  }

  initLoadBehaviors(behaviors: PatientBehaviorModel[]) {
    this.allBehaviors = behaviors.filter(b => !b.IsDeleted);
    this.sourceBehaviors = this.allBehaviors.filter((b) => true);
    this.destinationBehaviors = this.allBehaviors.filter((b) => true);

    this.behaviorsToMerge = [
      {
        BehaviorIDToMerge: 0,
        BehaviorIDToMergeInto: 0,
        destinationBehaviors: this.destinationBehaviors.filter((b) => true)
      }
    ]
  }

  onSourceBehaviorChange(behavior: { BehaviorIDToMerge?: number; BehaviorIDToMergeInto?: number; destinationBehaviors: PatientBehaviorModel[]; }) {
    console.log(behavior);
    behavior.destinationBehaviors = this.allBehaviors.filter((b) => b.ID !== behavior.BehaviorIDToMerge && b.BehaviorType === this.allBehaviors.find((b) => b.ID === behavior.BehaviorIDToMerge)?.BehaviorType);
  }

  addBehavior() {
    this.behaviorsToMerge.push({
      BehaviorIDToMerge: 0,
      BehaviorIDToMergeInto: 0,
      destinationBehaviors: this.allBehaviors.filter((b) => true)
    });
  }

  mergeBehaviors() {
    if (this.behaviorsToMerge.length <= 0) {
      this.alertService.error('Please select at least one behavior to merge');
      return;
    }
    for (const b of this.behaviorsToMerge) {
      if (b.BehaviorIDToMerge === 0 || b.BehaviorIDToMergeInto === 0) {
        this.alertService.error('Please select a behavior to merge and a behavior to merge into');
        return;
      }
    }

    this.alertService.confirm('Are you sure you want to merge the selected behaviors data and delete the source behaviors? This operation cannot be undone.', 'Confirm').subscribe({
      next: (result: boolean) => {
        if (result) {
          this.loading = true;
          this.supervisorApiService.MergeBehaviors({
          body: {
            MergeBehaviors: this.behaviorsToMerge
          }
        }).subscribe({
          next: (response) => {
            this.loading = false;
            this.alertService.success('Behaviors merged successfully');
            this.router.navigate(['/supervisor', this.patientID, 'behaviors']);
          },
          error: (error) => {
            this.loading = false;
            this.alertService.error('Error merging behaviors');
            console.log(error);
          }
          });
        }
      }
    });
  }

  getBehaviorTypeLabel(behaviorType: any): string {
    return Utils.patientBehaviorTypeLabel(behaviorType);
  }
} 